import { useEffect, useMemo, useState } from 'react'

import SettingsContainer from 'src/features/settings/components/ui/settings-container'
import SettingsFooter from 'src/features/settings/components/ui/settings-footer'
import SettingsHeader from 'src/features/settings/components/ui/settings-header'
import SettingsMobileNavigation from 'src/features/settings/components/ui/settings-mobile-navigation'
import { Button } from 'src/shared/components/ui/buttons/button'
import useGetEnterpriseMemberIds from 'src/shared/features/enterprise/api/use-get-enterprise-member-ids'
import { LeadDistributionPreference } from 'src/shared/features/enterprise/api/use-update-enterprise-settings'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import type { EnterpriseCompanyTypeValues } from 'src/shared/schemas/forms/auth'
import { cn } from 'src/shared/utils/cn'

import useCreateLeadDistribution from '../api/use-create-lead-distribution'
import useEditLeadDistribution from '../api/use-edit-lead-distribution'
import type { EnterpriseDistributionSystemResponse } from '../api/use-lead-distributions'
import { RandomIcon } from '../icons/random-icon'
import { RoundRobinIcon } from '../icons/round-robin-icon'

import LeadDistributionMemberPicker from './lead-distribution-member-picker'

type LeadDistributionFormProps = {
  mode: 'create' | 'edit'
  enterpriseId: number
  companyType: EnterpriseCompanyTypeValues | undefined
  initial?: EnterpriseDistributionSystemResponse
  onDone: () => void
}

function LeadDistributionForm({ mode, enterpriseId, companyType, initial, onDone }: LeadDistributionFormProps) {
  const { toast } = useToast()
  const { isTabletOrSmaller } = useBreakpoint()
  const { data: allMemberIds } = useGetEnterpriseMemberIds({ enterpriseId })
  const { mutateAsync: createLeadDistribution, isPending: isCreating } = useCreateLeadDistribution()
  const { mutateAsync: editLeadDistribution, isPending: isEditing } = useEditLeadDistribution()

  const [name, setName] = useState('')
  const [distributionType, setDistributionType] = useState<LeadDistributionPreference>(
    LeadDistributionPreference.RANDOM,
  )
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])

  useEffect(() => {
    if (mode === 'edit' && initial) {
      setName(initial.name)
      setDistributionType(initial.distributionType)
      setSelectedUserIds([...initial.memberUserIds])
    } else if (mode === 'create') {
      setName('')
      setDistributionType(LeadDistributionPreference.RANDOM)
      setSelectedUserIds([])
    }
  }, [mode, initial])

  const originalSnapshot = useMemo(() => {
    if (mode !== 'edit' || !initial) return null
    return {
      name: initial.name,
      distributionType: initial.distributionType,
      memberUserIds: [...initial.memberUserIds].sort((a, b) => a - b).join(','),
    }
  }, [mode, initial])

  const isDirty = useMemo(() => {
    if (mode === 'create') {
      return name.trim().length > 0 || selectedUserIds.length > 0
    }
    if (!originalSnapshot) return false
    const sorted = [...selectedUserIds].sort((a, b) => a - b).join(',')
    return (
      name.trim() !== originalSnapshot.name ||
      distributionType !== originalSnapshot.distributionType ||
      sorted !== originalSnapshot.memberUserIds
    )
  }, [mode, originalSnapshot, name, distributionType, selectedUserIds])

  const canSubmitCreate = name.trim().length > 0 && selectedUserIds.length > 0
  const canSubmitEdit = mode === 'edit' && initial && name.trim().length > 0 && selectedUserIds.length > 0 && isDirty

  const isPending = isCreating || isEditing

  const handleDiscard = () => {
    onDone()
  }

  const handleSubmitCreate = async () => {
    if (!canSubmitCreate || enterpriseId <= 0) return
    try {
      await createLeadDistribution({
        enterpriseId,
        name: name.trim(),
        distributionType,
        memberUserIds: selectedUserIds,
      })
      toast({ title: 'Distribution created' })
      onDone()
    } catch {
      toast({ title: 'Could not create distribution', variant: 'destructive' })
    }
  }

  const handleSubmitEdit = async () => {
    if (!initial || enterpriseId <= 0 || !canSubmitEdit) return
    try {
      await editLeadDistribution({
        enterpriseId,
        distributionSystemId: initial.id,
        name: name.trim(),
        distributionType,
        memberUserIds: selectedUserIds,
      })
      toast({ title: 'Changes saved' })
      onDone()
    } catch {
      toast({ title: 'Could not save changes', variant: 'destructive' })
    }
  }

  const title = mode === 'create' ? 'Create new distribution' : 'Edit distribution'
  const nameLabel = mode === 'create' ? 'Name your distribution' : 'Name'

  return (
    <div className="relative flex size-full min-h-0 flex-col pb-[100px] pt-[70px]">
      <SettingsMobileNavigation isEnterprise />

      <SettingsContainer className="w-full flex-1">
        <div
          className={cn(
            'grid gap-8 pb-4',
            isTabletOrSmaller
              ? 'grid-cols-1'
              : 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,450px)] lg:items-start',
          )}
        >
          <div className="flex max-w-[600px] flex-col gap-7">
            <div className="flex flex-col gap-5">
              <div className="mb-4 flex flex-col gap-1">
                <SettingsHeader
                  className="p-0"
                  title={title}
                  subtitle="Create and manage how leads are distributed among your team."
                />
              </div>

              <div className="flex flex-col gap-4">
                <label htmlFor="lead-dist-name" className="text-base font-semibold text-primary">
                  {nameLabel}
                </label>
                <input
                  id="lead-dist-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={mode === 'create' ? 'Type here' : undefined}
                  className="h-[55px] w-full max-w-[450px] rounded-[12px] border-none bg-accent px-4 text-[15px] font-medium text-primary outline-none ring-1 ring-transparent transition-shadow placeholder:text-muted"
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-base font-semibold text-primary">Distribution type</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDistributionType(LeadDistributionPreference.RANDOM)}
                  className={cn(
                    'flex h-[65px] items-center justify-center gap-3 rounded-[20px] px-8 py-3.5 text-base font-medium transition-colors',
                    distributionType === LeadDistributionPreference.RANDOM
                      ? 'bg-primary text-white'
                      : 'bg-accent text-primary',
                  )}
                >
                  <RandomIcon size={25} strokeWidth={2} className="shrink-0" />
                  Random
                </button>
                <button
                  type="button"
                  onClick={() => setDistributionType(LeadDistributionPreference.ROUND_ROBIN)}
                  className={cn(
                    'flex h-[65px] items-center justify-center gap-3 rounded-[20px] px-8 py-3.5 text-base font-medium transition-colors',
                    distributionType === LeadDistributionPreference.ROUND_ROBIN
                      ? 'bg-primary text-white'
                      : 'bg-accent text-primary',
                  )}
                >
                  <RoundRobinIcon size={21} strokeWidth={2} className="shrink-0" />
                  Round robin
                </button>
              </div>
            </div>
          </div>

          <LeadDistributionMemberPicker
            enterpriseId={enterpriseId}
            allMemberIds={allMemberIds}
            companyType={companyType}
            selectedUserIds={selectedUserIds}
            onSelectedUserIdsChange={setSelectedUserIds}
          />
        </div>
      </SettingsContainer>

      <SettingsFooter className="flex flex-row items-center justify-between">
        {mode === 'create' ? (
          <>
            <button
              type="button"
              onClick={handleDiscard}
              className={cn(
                'text-[15px] font-semibold text-primary underline transition-opacity',
                isPending && 'opacity-40',
              )}
              disabled={isPending}
            >
              Discard
            </button>
            <Button
              variant="default"
              size="default"
              className={cn('h-[52px] rounded-full px-8 text-[15px]', isTabletOrSmaller ? 'min-w-0' : 'min-w-[180px]')}
              onClick={handleSubmitCreate}
              disabled={!canSubmitCreate || isPending}
              isLoading={isPending}
            >
              Create
            </Button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={handleDiscard}
              className={cn('text-sm font-semibold text-primary transition-opacity', isPending && 'opacity-40')}
              disabled={isPending}
            >
              Discard changes
            </button>
            <Button
              variant="default"
              size="default"
              className={cn('h-[52px] rounded-full px-8', isTabletOrSmaller ? 'min-w-0' : 'min-w-[180px]')}
              onClick={handleSubmitEdit}
              disabled={!canSubmitEdit || isPending}
              isLoading={isPending}
            >
              Save changes
            </Button>
          </>
        )}
      </SettingsFooter>
    </div>
  )
}

export default LeadDistributionForm
